import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import { numberToABC } from '@doltech/utils/lib/text';
import cl from 'classnames';
import * as React from 'react';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import { CircleText, QuestionDisplayMode } from '../CircleText/CircleText';
import { namedColors } from '../colors';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { DragItem } from '../FloatingDraggableBox/FloatingDraggableBox';
import { SmallCloseButton, SpotItem, SpotOrder, SpotPlaceholder } from './SpotEditor';
import { ResizableImage, Spot } from './types';
import { shadows } from '../shadows';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { ScrollableOptionListLayout } from '../Options/ScrollableOptionListLayout';
import { OptionListWithTitleLayout } from '../Options/OptionListWithTitleLayout';

// Ignore old file
/* stylelint-disable */
const SPOT_CONTAINER_HEIGHT = 46;

const FloatingRight = styled.div`
  position: sticky;
  top: 0;

  background-color: ${colorsV2.white100};
  border-radius: 8px;
  box-shadow: ${shadows.zIndex4};

  &.outer {
    margin-left: 0;
    ${fromScreen(1024)} {
      transform: translateX(calc(100% + 30px));
    }
  }
`;

export interface DragInProps extends React.HTMLAttributes<HTMLDivElement> {
  no?: number;
  spots: Spot[];
  image: ResizableImage;
  options: string[];
  answered: number[];
  onRemoveAnswer: (idx: number) => void;
  onAnswer: (idx: number, optionIdx: number) => void;
  scaledWidth?: number;
  scaledHeight?: number;
}

const DragInContext = React.createContext<DragInProps>(null);

const DragIn = (props: DragInProps, floatingBoxRef) => {
  const { options, id } = props;

  const [showOptions, setShowOptions] = React.useState(false);

  React.useEffect(() => {
    const clientHeight = document.getElementById(id)?.clientHeight;
    if (clientHeight) {
      setShowOptions(true);
    }
  }, [id]);

  return (
    <DragInContext.Provider value={props}>
      <Container id={id}>
        <Image />
        {showOptions && (
          <FloatingRight className="outer">
            <OptionList>
              <OptionListWithTitleLayout title="Kéo option vào trong hình">
                <ScrollableOptionListLayout>
                  {options.map((option, idx) => (
                      <DragItem
                          key={option}
                          dragData={{ idx, text: option }}
                          accept={QuestionTypeEnum.DRAG_IN}
                          preview={false}
                      >
                        <CircleText
                            displayMode={QuestionDisplayMode.QUESTION_OPTION_DRAG}
                            circleText={numberToABC(idx + 1)}
                            text={option}
                        />
                      </DragItem>
                  ))}
                </ScrollableOptionListLayout>
              </OptionListWithTitleLayout>

            </OptionList>
          </FloatingRight>
        )}
      </Container>
    </DragInContext.Provider>
  );
};

const Container = styled.div`
  position: relative;
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr 0;
  gap: 16px;
  .content .list-item {
    padding: 4px;

    &.dragged,
    &.dragging {
      .circle-text .question-circle.question-option-drag {
        background-color: ${colorsV2.blue60};
        border: none;
      }
    }

    .circle-text {
      padding: 0;

      .question-circle.question-option-drag {
        border: none;
        background-color: ${colorsV2.blue100};

        & > span {
          color: ${colorsV2.white100};
          font-size: 12px;
          font-weight: 600;
        }
      }
    }
  }
`;

export const OPTION_LIST_WIDTH = 268;

const OptionList = styled.div`
  width: ${OPTION_LIST_WIDTH}px;
  background-color: ${namedColors.pureWhite};
  box-shadow: 0 16px 24px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  border-radius: 8px;

  z-index: 2;
  .question-text > span {
    font-weight: 400;
  }
`;

export default React.memo(React.forwardRef(DragIn));

// =================================================================================
// DraggableSpot
// =================================================================================

interface DragData {
  dragData: {
    idx: number;
    text: string;
  };
}

const DroppableSpot = ({ question, idx, onRemove, ...rest }) => {
  const { onAnswer, no, answered, options } = React.useContext(DragInContext);

  const [{ isOver }, drop] = useDrop({
    accept: QuestionTypeEnum.DRAG_IN,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop: (item: DragData) => {
      onAnswer(idx, item.dragData.idx);
    },
  });

  const isAnswered = typeof answered?.[idx] === 'number';
  const order = idx + no;
  const spotContainerRef = React.useRef(null);
  const [topReduce, setTopReduce] = React.useState(0);

  return (
    <DroppableSpotContainer
      top={question.y - 5} // Subtract 5px for the wrapper div contain answered text.
      left={question.x}
      topReduce={topReduce}
      onMouseOver={() => {
        setTopReduce(
          (spotContainerRef.current.getBoundingClientRect().height - SPOT_CONTAINER_HEIGHT) / 2
        );
      }}
      ref={spotContainerRef}
    >
      <SpotItem
        ref={drop}
        text={order}
        className={cl('spot-item', {
          'is-over': isOver,
          answered: isAnswered,
        })}
        {...rest}
      >
        {isAnswered ? (
          <AnswerWrapper className="answer-wrapper">
            <Answer className="answer-order">{numberToABC(answered[idx] + 1)}</Answer>
            <div className="answer-text">
              <Typography.Text variant="regular/14">{options[answered[idx]]}</Typography.Text>
              <SmallCloseButton className="answer-close-btn" onClick={onRemove} />
            </div>
          </AnswerWrapper>
        ) : (
          <SpotPlaceholder className="drop-circle with-answer-text" />
        )}
      </SpotItem>
    </DroppableSpotContainer>
  );
};

export const MobileSpot = ({ question, idx, onRemove, no, answered, ...rest }) => {
  const isAnswered = typeof answered[idx] === 'number';
  const order = idx + no;

  return (
    <DroppableSpotContainer top={question.y} left={question.x} topReduce={0}>
      <SpotItem
        text={order}
        className={cl('spot-item', {
          answered: isAnswered,
        })}
        {...rest}
      />
    </DroppableSpotContainer>
  );
};

const AnswerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px 4px 4px;
  border: 1px solid transparent;
  border-radius: 16px;
  .answer-text {
    flex-direction: row;
    align-items: center;
    margin-left: 8px;
    display: none;
    .ant-typography {
      width: max-content;
      max-width: 194px;
      overflow-wrap: break-word;
    }
  }
  .answer-close-btn {
    margin-left: 8px;
  }
`;

const Answer = styled(SpotOrder)`
  &&& {
    background-color: ${colorsV2.blue100};
  }

  &:hover {
    .answer {
      display: block;
    }
  }
`;

const DroppableSpotContainer = styled.div<{ top; left; topReduce }>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  top: ${(p) => p.top}px;
  left: ${(p) => p.left}px;

  .spot-item,
  .spot-item:hover {
    &.is-over {
      .order {
        background: ${colorsV2.black60};
      }
      .drop-circle,
      .answer-order {
        background-color: ${colorsV2.blue20};
        border: 1px dashed ${colorsV2.blue40};
      }
    }
  }

  &:hover {
    z-index: 2;
    top: ${(p) => p.top - p.topReduce}px;
    left: ${(p) => p.left}px;
    .answer-wrapper {
      border-color: ${colorsV2.gray20};
      background: ${colorsV2.white100};
      box-sizing: border-box;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
        0 0 1px rgba(0, 0, 0, 0.04);
      .answer-text {
        display: inline-flex;
      }
    }
  }
`;

// =================================================================================
// Image
// =================================================================================

const Image = () => {
  const { no, spots, image, onRemoveAnswer } = React.useContext(DragInContext);
  const { width, height, url } = image;

  return (
    <ImgBorder key={url}>
      <Img loading="lazy" key={image.url} src={image.url} alt="" style={{ width, height }} />
      {spots.map((question: Spot, idx: number) => {
        return (
          <DroppableSpot
            id={`question-no-${no + idx}`}
            question={question}
            idx={idx}
            key={question.id}
            onRemove={() => onRemoveAnswer(idx)}
          />
        );
      })}
    </ImgBorder>
  );
};

const ImgBorder = styled.div`
  margin: 0 auto;
  width: fit-content;
  background: ${namedColors.pureWhite};
  box-sizing: border-box;
  border-radius: 8px;
  position: relative;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
`;
